import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAppContext } from "../AppContextDef";
import MainCard from "../components/MainCard";
import { useTranslation } from "react-i18next";
import useIsMobile from "../utils/useIsMobile";
import useIsTab from "../utils/useIsTab";
import { usePubSub, useMeeting } from "@videosdk.live/react-sdk";
import usePubsubFunction from "../utils/usePubsubFunction";
import { fontMap, logType, useJourney } from "../utils/common";

import useAuthFetch from "../utils/useAuthFetch";
import ConfirmBox from "../components/ConfirmBox";
import { Bounce, toast } from "react-toastify";
import ParticipantView from "../components/ParticipantView";

const Qna = ({
  handleAgree,
  handleDisagree,
  currentPage,
  capturedImage,
  setCapturedImage,
  abortSignal,
  getScreenshotImage,
}) => {
  const {
    qna,
    selectedLanguageCode,
    HeaderSectionHeight,
    allDetails,
  } = useAppContext();
  const { t, i18n } = useTranslation();
  let isMobile = useIsMobile();
  let isTab = useIsTab();
  const [buttonSectionHeightRef, setButtonSectionHeightRef] = useState(null);
  const { logJourney, updateJourney, endJourney } = useJourney();
  const {
    selectedLang,
  } = useAppContext();
  const fontFamilyCSS = fontMap[selectedLang?.lang] || "Lato";
  const { end } = useMeeting();
  const { publish: endJourneyAPIWaitingPublish } = usePubSub(
    "END_JOURNEY_API_WAITING"
  );
  const { publish: terminationStatusPublish } = usePubSub(
    "TERMINATION_STATUS"
  );
  const pubsubPublishMethod = usePubsubFunction();

  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [openError, setOpenError] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const ButtonSectionHeight = useMemo(
    () => buttonSectionHeightRef?.offsetHeight,
    [buttonSectionHeightRef]
  );

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    changeLanguage(selectedLanguageCode);
  }, [selectedLanguageCode]);

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  let isMobileOrTab = isMobile || isTab;
  let spacingHeight = isMobile
    ? HeaderSectionHeight + ButtonSectionHeight + 180 + 70 + 20
    : isTab
      ? HeaderSectionHeight + ButtonSectionHeight + 290 + 70 + 40
      : ButtonSectionHeight + 160 + 56 + 40;

  let videoHeight = viewportHeight - spacingHeight;


  // Mock data handling if allDetails.qna is undefined for safe rendering
  const qnaList = qna || [];
  const allAnswered = useMemo(() => {
    // If no questions, consider it answered (allow journey to complete)
    if (qnaList.length === 0) {
      return true;
    }
    // If questions exist, all must be answered
    return qnaList.every((item) => answers[item.id]);
  }, [qnaList, answers]);

  const { publish: qnaDataPublish } = usePubSub("QNA_ANSWERS");
  const customerImageRef = useRef(null); // Assuming image is handled via props or ref if needed

  useEffect(() => {
    if (capturedImage) {
      customerImageRef.current = capturedImage;
    }
  }, [capturedImage]);

  const handleAnswerChange = (questionId, answer, question) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { answer, question, questionId },
    }));
    if (qnaDataPublish) {
      pubsubPublishMethod(qnaDataPublish, "QNA_ANSWER", { persist: true }, { questionId, answer, question });
    }
  };

  const endJourneyAPI = async (journeyStatus, showTerminationMessage = false) => {
    const status = await endJourney({
      journeyStatus: journeyStatus,
    });

    if (status === 200) {
      pubsubPublishMethod(endJourneyAPIWaitingPublish, "journeyend", { persist: true }, { isJourneyEnd: false });
      end();
      
      // Store termination flag to show different message
      if (showTerminationMessage) {
        sessionStorage.setItem("showTerminationMessage", "true");
      }
      
      if (handleAgree) handleAgree();
    }
  };

  const handlesubmit = async () => {
    // Validate if all questions are answered (only if questions exist)
    if (qnaList.length > 0) {
      const unanswered = qnaList.filter((item) => !answers[item.id]);
      if (unanswered.length > 0) {
        toast.error(t("Please answer all questions to proceed."), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
          style: { fontFamily: fontFamilyCSS },
        });
        return;
      }
    }

    setLoading(true);
    try {
      // Submit QNA answers (empty array if no questions)
      const { response, status, message } = await updateJourney({
        journeyQna: qnaList.length > 0 ? [...Object.values(answers)] : [],
      });
      
      if (status === 200) {
        // Check if journey should be stopped due to NO answer on Non-NO type question
        const shouldStop = response?.shouldStopJourney === true;
        
        if (shouldStop) {
          if (terminationStatusPublish) {
            pubsubPublishMethod(
              terminationStatusPublish,
              "TERMINATED",
              { persist: true },
              { showTerminationMessage: true }
            );
          }
          logJourney({
            type: logType.ACTION,
            message: "QnA Submitted - Journey terminated due to NO answer on Non-NO type question",
          });
          await endJourneyAPI("FAILED", true); // Show termination message
          return;
        }

        // Continue normally - show success screen
        logJourney({
          type: logType.ACTION,
          message: "QnA Submitted Successfully",
        });
        await endJourneyAPI("COMPLETED", false);
      } else {
        setErrorMsg(message || t("Something went wrong"));
        setOpenError(true);
      }

    } catch (e) {
      setErrorMsg(t("Something went wrong"));
      setOpenError(true);
      logJourney({
        type: logType.ERROR,
        message: `QnA Submission Failed: ${e.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col-reverse lg:flex-row flex-1 py-3 px-3 lg:py-9 lg:px-8 xl:px-14 gap-3 md:gap-4 lg:gap-5 xl:gap-16">
      <MainCard
        classNameMain="lg:w-1/2 xl:w-1/2"
        classNameFirstSection="h-full flex flex-col"
        mainTitle={t("QnA")}
        classNameTitleContainer="flex-none"
        FirstSection={
          <div
            style={{
              height: `${viewportHeight - spacingHeight}px`,
            }}
            className="mt-3 flex-grow lg:mt-14 px-1 lg:px-0 overflow-y-auto scrollable-content scrollbar scrollbar-primary"
          >
            {qnaList.map((item, index) => (
              <div key={item.id} className="flex flex-col flex-1 mb-4 border-b pb-4 last:border-b-0">
                <p
                  style={{ fontFamily: fontFamilyCSS }}
                  className={`text-sm md:text-base lg:text-lg font-semibold text-black-150 mb-2 ${!agreed ? "opacity-50" : ""}`}
                >
                  {index + 1}. {item.question}
                </p>
                <div className="flex gap-4">
                  {item.answers?.map((answer, ansIndex) => (
                    <div key={ansIndex} className="flex items-center">
                      <input
                        type="radio"
                        id={`q${item.id}-a${ansIndex}`}
                        name={`q${item.id}`}
                        value={answer}
                        checked={answers[item.id]?.answer === answer}
                        onChange={() => handleAnswerChange(item.id, answer, item.question)}
                        disabled={!agreed}
                        className="w-4 h-4 text-orange-150 border-gray-300 focus:ring-orange-150"
                      />
                      <label
                        htmlFor={`q${item.id}-a${ansIndex}`}
                        style={{ fontFamily: fontFamilyCSS }}
                        className={`ml-2 text-sm text-gray-700 ${!agreed ? "opacity-50" : ""}`}
                      >
                        {answer}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {qnaList.length === 0 && (
              <p
                style={{ fontFamily: fontFamilyCSS }}
                className="text-center text-gray-500 mt-10"
              >
                {t("No questions available.")}
              </p>
            )}
          </div>
        }
        SecondSection={
          <div
            ref={setButtonSectionHeightRef}
            className="flex mt-3 lg:mt-10 px-1 lg:px-0 justify-between"
          >
            <div className="flex flex-1 text-sm md:text-base">
              <button
                style={{ fontFamily: fontFamilyCSS }}
                className={`bg-orange-150 text-white p-2 md:p-3 rounded-md w-full font-bold lg:max-w-max ${loading || !allAnswered ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                disabled={loading || !allAnswered}
                onClick={handlesubmit}
              >
                {loading ? t("Submitting...") : t("Submit")}
              </button>
            </div>
          </div>
        }
      />
      <div className="lg:w-1/2 xl:w-1/2 h-full flex flex-col">
        <ParticipantView
          capturedImage={capturedImage}
          setCapturedImage={setCapturedImage}
          setAgreed={setAgreed}
          agreed={agreed}
          currentPage={currentPage}
          abortSignal={abortSignal}
          videoHeight={videoHeight}
        />
      </div>

      {openError && (
        <ConfirmBox
          open={openError}
          setOpen={setOpenError}
          text={errorMsg}
          buttonText={t("Close")}
          buttonHandler={() => setOpenError(false)}
        />
      )}
    </div>
  );
};

export default Qna;
